import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading text-foreground-900">文章不存在</h1>
          <Link to="/" className="text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const categoryMap: Record<string, string> = {
    'baisi-culture': '白丝文化',
    'trend-outfit': '潮流穿搭',
    'buying-guide': '选购指南',
    'baisi-aesthetic': '白丝美学',
  };

  const catName = categoryMap[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const catInfo = categories.find((c) => c.slug === article.category);
  const heroImageMap: Record<string, string> = {
    'article-baisi-origin': 'https://readdy.ai/api/search-image?query=Elegant%20white%20stockings%20fashion%20editorial%20photography%20with%20soft%20lighting%2C%20minimalist%20aesthetic%2C%20clean%20cream%20background%2C%20artistic%20composition%2C%20delicate%20texture&width=1200&height=630&seq=article-hero-origin&orientation=landscape',
    'article-baisi-anime': 'https://readdy.ai/api/search-image?query=Anime%20inspired%20white%20stockings%20illustration%20with%20soft%20pastel%20colors%2C%20editorial%20style%2C%20clean%20composition%2C%20delicate%20lighting&width=1200&height=630&seq=article-hero-anime&orientation=landscape',
    'article-baisi-global': 'https://readdy.ai/api/search-image?query=Global%20fashion%20trend%20white%20stockings%20street%20photography%2C%20modern%20urban%20setting%2C%20natural%20light%2C%20editorial%20quality&width=1200&height=630&seq=article-hero-global&orientation=landscape',
    'article-baisi-jk': 'https://readdy.ai/api/search-image?query=Japanese%20school%20uniform%20style%20with%20white%20knee%20high%20socks%2C%20academy%20aesthetic%2C%20clean%20minimalist%20photography&width=1200&height=630&seq=article-hero-jk&orientation=landscape',
    'article-baisi-lolita': 'https://readdy.ai/api/search-image?query=Lolita%20fashion%20with%20white%20lace%20stockings%2C%20elegant%20dress%2C%20vintage%20tea%20party%2C%20soft%20romantic%20lighting&width=1200&height=630&seq=article-hero-lolita&orientation=landscape',
    'article-baisi-seasons': 'https://readdy.ai/api/search-image?query=Four%20seasons%20white%20stockings%20fashion%20outfit%2C%20seasonal%20style%20variations%2C%20clean%20minimal%20photography&width=1200&height=630&seq=article-hero-seasons&orientation=landscape',
    'article-baisi-commute': 'https://readdy.ai/api/search-image?query=Professional%20office%20lady%20white%20stockings%20commute%20style%2C%20business%20casual%2C%20modern%20city%20background&width=1200&height=630&seq=article-hero-commute&orientation=landscape',
    'article-baisi-choose': 'https://readdy.ai/api/search-image?query=Various%20white%20stockings%20display%20minimalist%20surface%2C%20product%20photography%2C%20different%20textures%2C%20clean%20cream%20background&width=1200&height=630&seq=article-hero-choose&orientation=landscape',
    'article-baisi-material': 'https://readdy.ai/api/search-image?query=White%20stockings%20fabric%20texture%20closeup%2C%20silk%20cotton%20nylon%20comparison%2C%20minimalist%20flat%20lay&width=1200&height=630&seq=article-hero-material&orientation=landscape',
    'article-baisi-brand': 'https://readdy.ai/api/search-image?query=Luxury%20white%20stockings%20brand%20packaging%2C%20elegant%20product%20photography%2C%20minimalist%20setup%2C%20cream%20background&width=1200&height=630&seq=article-hero-brand&orientation=landscape',
    'article-baisi-photography': 'https://readdy.ai/api/search-image?query=Artistic%20white%20stockings%20photography%20dramatic%20lighting%2C%20shadow%20and%20light%20play%2C%20creative%20composition&width=1200&height=630&seq=article-hero-photography&orientation=landscape',
    'article-baisi-oriental': 'https://readdy.ai/api/search-image?query=Oriental%20aesthetic%20white%20silk%20stockings%20traditional%20Chinese%20elements%2C%20ink%20wash%20painting%20style&width=1200&height=630&seq=article-hero-oriental&orientation=landscape',
  };
  const imageUrl = heroImageMap[article.imageSeq] || heroImageMap['article-baisi-origin'];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://baisi-style.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '白丝文化指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://baisi-style.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title}｜白丝文化指南`}
        description={article.excerpt}
        keywords={`白丝,${article.title},白丝搭配,白丝风格,${catName},白丝美学,白丝文化`}
        canonicalUrl={`https://baisi-style.com/article/${article.slug}`}
        ogType="article"
        schema={schema}
      />
      <Navbar />

      <main className="pt-16">
        <article>
          <header className="w-full bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: catName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
              <div className="mt-6">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-block px-3 py-1 text-xs bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap cursor-pointer"
                >
                  {catName}
                </Link>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center text-sm text-foreground-500 space-x-4">
                  <span className="flex items-center">
                    <i className="ri-user-line mr-1.5"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center">
                    <i className="ri-calendar-line mr-1.5"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center">
                    <i className="ri-time-line mr-1.5"></i>
                    {article.readTime}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8">
            <div className="rounded-lg overflow-hidden mb-10">
              <img
                src={imageUrl}
                alt={article.title}
                title={`${article.title} - 白丝文化指南`}
                className="w-full h-auto object-cover max-h-[500px]"
              />
            </div>

            <div
              className="article-content max-w-none"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />
          </div>

          <footer className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-10">
            <div className="border-t border-background-200/70 pt-8 mt-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-foreground-500">分享文章：</span>
                  <a
                    href="#"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                    rel="nofollow"
                    aria-label="分享到微博"
                  >
                    <i className="ri-weibo-line"></i>
                  </a>
                  <a
                    href="#"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                    rel="nofollow"
                    aria-label="分享到微信"
                  >
                    <i className="ri-wechat-line"></i>
                  </a>
                  <a
                    href="#"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                    rel="nofollow"
                    aria-label="复制链接"
                  >
                    <i className="ri-link"></i>
                  </a>
                </div>
                <Link
                  to={`/category/${article.category}`}
                  className="flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line mr-1"></i>
                  返回{catName}
                </Link>
              </div>
            </div>
          </footer>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.slug} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}